import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

type Theme = 'light' | 'dark'

// ── CSS ─────────────────────────────────────────────────────────────────────

const FOR_CSS = `
#for-root{--ink:#0f1016;--muted:#636672;--faint:#9a9caa;--line:#e8e9f0;--bg:#f7f7fa;--paper:#ffffff;--accent:#6366f1;--accent-2:#4f46e5;--soft:#eef0ff;--fb:'Inter',-apple-system,'Segoe UI',sans-serif;--fd:'Inter Tight',-apple-system,'Segoe UI',sans-serif}
#for-root[data-theme="dark"]{--ink:#e8eaf6;--muted:#888ba8;--faint:#454660;--line:#252640;--bg:#111220;--paper:#18192c;--accent:#6366f1;--accent-2:#818cf8;--soft:#1c1e40}

#for-root *{margin:0;padding:0;box-sizing:border-box}
#for-root{font-family:var(--fb);font-size:15px;line-height:1.6;color:var(--ink);background:var(--bg);-webkit-font-smoothing:antialiased;min-height:100vh}
#for-root a{text-decoration:none;color:inherit}
#for-root button{font-family:inherit;border:0;background:none;cursor:pointer;color:inherit}
#for-root :focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:6px}
#for-root ::selection{background:var(--accent);color:#fff}
#for-root .container{max-width:1080px;margin:0 auto;padding:0 24px}

/* reveal animations */
#for-root .reveal{opacity:0;transform:translateY(18px);transition:opacity .7s cubic-bezier(.2,.6,.2,1),transform .7s cubic-bezier(.2,.6,.2,1);transition-delay:var(--d,0s)}
#for-root .reveal.in{opacity:1;transform:none}

/* header */
#for-root header{position:sticky;top:0;z-index:60;background:rgba(255,255,255,.85);-webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);border-bottom:1px solid transparent;transition:border-color .3s,background .3s}
#for-root header.scrolled{border-color:var(--line);box-shadow:0 4px 20px rgba(15,16,40,.06)}
#for-root[data-theme="dark"] header{background:rgba(17,18,32,.9)}
#for-root[data-theme="dark"] header.scrolled{box-shadow:0 4px 20px rgba(0,0,0,.3)}
#for-root .h-in{display:flex;align-items:center;gap:12px;height:62px}
#for-root .brand{display:flex;align-items:center;gap:8px;cursor:pointer}
#for-root .brand-mark{width:26px;height:26px;border-radius:8px;background:var(--accent);color:#fff;font-size:12px;font-weight:800;display:grid;place-items:center;flex-shrink:0}
#for-root .brand-name{font-family:var(--fd);font-weight:800;font-size:15px;letter-spacing:-.02em;color:var(--ink)}
#for-root .sep{color:var(--line);font-size:18px;margin:0 2px}
#for-root .crumb{font-size:13px;color:var(--muted);font-weight:500}
#for-root .h-right{margin-left:auto;display:flex;align-items:center;gap:10px}
#for-root .theme-toggle{display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:9px;border:1px solid var(--line);color:var(--muted);background:var(--paper);transition:all .2s}
#for-root .theme-toggle:hover{border-color:var(--accent);color:var(--accent)}
#for-root .btn-h{font-size:13px;font-weight:700;padding:7px 16px;border-radius:9px;transition:all .2s;font-family:var(--fb)}
#for-root .btn-h.primary{background:var(--accent);color:#fff;box-shadow:0 6px 18px rgba(99,102,241,.28)}
#for-root .btn-h.primary:hover{background:var(--accent-2);transform:translateY(-1px)}
#for-root .btn-h.ghost{border:1px solid var(--line);color:var(--muted)}
#for-root .btn-h.ghost:hover{border-color:rgba(99,102,241,.35);color:var(--ink)}

/* hero */
#for-root .hero{padding:80px 0 72px;background:var(--paper);border-bottom:1px solid var(--line);text-align:center;position:relative;overflow:hidden}
#for-root .hero::before{content:"";position:absolute;inset:0;background-image:radial-gradient(#e2e4f0 1px,transparent 1.4px);background-size:22px 22px;-webkit-mask-image:linear-gradient(#000 5%,transparent 75%);mask-image:linear-gradient(#000 5%,transparent 75%);pointer-events:none;opacity:.55}
#for-root[data-theme="dark"] .hero::before{background-image:radial-gradient(#252640 1px,transparent 1.4px)}
#for-root .hero::after{content:"";position:absolute;inset:0;background:radial-gradient(640px 280px at 50% -40px,rgba(99,102,241,.09),transparent 70%);pointer-events:none}
#for-root .hero .container{position:relative;z-index:1}
#for-root .niche-badge{display:inline-flex;align-items:center;gap:8px;font-size:12px;font-weight:600;letter-spacing:.04em;color:var(--accent);background:var(--soft);border:1px solid rgba(99,102,241,.22);border-radius:999px;padding:6px 14px;margin-bottom:24px}
#for-root .niche-emoji{font-size:32px;line-height:1;margin-bottom:20px;display:block}
#for-root .hero h1{font-family:var(--fd);font-weight:800;font-size:clamp(32px,5.5vw,58px);letter-spacing:-.04em;line-height:1.04;color:var(--ink);margin-bottom:18px}
#for-root .hero h1 .acc{color:var(--accent)}
#for-root .hero-sub{color:var(--muted);font-size:clamp(15px,2vw,17px);max-width:560px;margin:0 auto 32px;line-height:1.65}
#for-root .hero-cta{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
#for-root .btn-primary{display:inline-flex;align-items:center;gap:8px;background:var(--accent);color:#fff;font-weight:700;font-size:14.5px;padding:13px 26px;border-radius:12px;border:0;cursor:pointer;font-family:var(--fb);transition:all .22s;box-shadow:0 10px 28px rgba(99,102,241,.3)}
#for-root .btn-primary:hover{background:var(--accent-2);transform:translateY(-2px)}
#for-root .btn-ghost-lg{display:inline-flex;align-items:center;gap:8px;background:var(--paper);color:var(--ink);font-weight:600;font-size:14.5px;padding:12px 24px;border-radius:12px;border:1px solid var(--line);cursor:pointer;font-family:var(--fb);transition:all .22s}
#for-root .btn-ghost-lg:hover{border-color:rgba(99,102,241,.35);transform:translateY(-2px)}
#for-root .hero-meta{display:flex;align-items:center;justify-content:center;gap:20px;flex-wrap:wrap;margin-top:28px}
#for-root .meta-pill{font-size:12px;color:var(--faint);display:flex;align-items:center;gap:6px}
#for-root .meta-dot{width:4px;height:4px;border-radius:50%;background:var(--accent);opacity:.6}

/* features */
#for-root .section{padding:80px 0}
#for-root .sec-head{text-align:center;margin-bottom:48px}
#for-root .kicker{display:block;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--accent);margin-bottom:10px}
#for-root .sec-title{font-family:var(--fd);font-weight:800;font-size:clamp(26px,4vw,38px);letter-spacing:-.03em;color:var(--ink);line-height:1.1}
#for-root .sec-sub{color:var(--muted);font-size:15.5px;margin-top:10px;max-width:520px;margin-left:auto;margin-right:auto;line-height:1.6}
#for-root .fgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
#for-root .fcell{background:var(--paper);border:1px solid var(--line);border-radius:14px;padding:24px;transition:box-shadow .2s,transform .2s}
#for-root .fcell:hover{box-shadow:0 12px 36px rgba(15,16,40,.08);transform:translateY(-3px)}
#for-root[data-theme="dark"] .fcell:hover{box-shadow:0 12px 36px rgba(0,0,0,.3)}
#for-root .f-ico{width:40px;height:40px;border-radius:11px;background:var(--soft);color:var(--accent);display:flex;align-items:center;justify-content:center;font-size:20px;margin-bottom:14px;flex-shrink:0}
#for-root .fcell h3{font-size:15px;font-weight:700;color:var(--ink);margin-bottom:7px}
#for-root .fcell p{font-size:13.5px;color:var(--muted);line-height:1.55}

/* testimonial */
#for-root .tinted{background:var(--bg)}
#for-root .quote-card{max-width:720px;margin:0 auto;background:var(--paper);border:1px solid var(--line);border-radius:18px;padding:40px 44px;position:relative}
#for-root .quote-card::before{content:'“';position:absolute;top:12px;left:28px;font-size:72px;line-height:1;color:var(--accent);opacity:.2;font-family:Georgia,serif;font-weight:900}
#for-root .quote-text{font-size:17px;color:var(--ink);line-height:1.65;font-style:italic;position:relative;z-index:1}
#for-root .quote-foot{display:flex;align-items:center;gap:14px;margin-top:24px}
#for-root .quote-av{width:44px;height:44px;border-radius:50%;background:var(--accent);color:#fff;font-weight:800;font-size:16px;display:grid;place-items:center;flex-shrink:0}
#for-root .quote-name{font-weight:700;font-size:14px;color:var(--ink)}
#for-root .quote-role{font-size:13px;color:var(--muted);margin-top:2px}

/* use-cases list */
#for-root .uc-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-top:0}
#for-root .uc-item{display:flex;align-items:flex-start;gap:10px;background:var(--paper);border:1px solid var(--line);border-radius:11px;padding:16px}
#for-root .uc-check{width:22px;height:22px;border-radius:7px;background:var(--soft);color:var(--accent);display:grid;place-items:center;flex-shrink:0;font-size:12px;font-weight:800;margin-top:1px}
#for-root .uc-text{font-size:13.5px;color:var(--ink);line-height:1.45;font-weight:500}

/* steps */
#for-root .steps{display:grid;grid-template-columns:repeat(3,1fr);gap:0;position:relative;margin-top:0}
#for-root .steps::before{content:"";position:absolute;top:24px;left:calc(16.66% + 12px);right:calc(16.66% + 12px);height:1px;background:var(--line);z-index:0}
#for-root .step{text-align:center;padding:0 20px;position:relative;z-index:1}
#for-root .step-num{display:inline-flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:50%;background:var(--paper);border:2px solid var(--accent);color:var(--accent);font-weight:800;font-size:15px;margin-bottom:16px;font-family:var(--fd)}
#for-root .step h3{font-size:15px;font-weight:700;color:var(--ink);margin-bottom:8px}
#for-root .step p{font-size:13.5px;color:var(--muted);line-height:1.55}

/* templates */
#for-root .tpl-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
#for-root .tpl-card{background:var(--paper);border:1px solid var(--line);border-radius:14px;overflow:hidden;cursor:pointer;transition:all .2s}
#for-root .tpl-card:hover{box-shadow:0 12px 36px rgba(99,102,241,.12);transform:translateY(-3px);border-color:rgba(99,102,241,.3)}
#for-root .tpl-thumb{height:160px;display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden}
#for-root .tpl-thumb-inner{width:100%;height:100%;display:flex;flex-direction:column;padding:12px 10px 0;gap:6px}
#for-root .tpl-bar{height:7px;border-radius:3px;background:var(--line)}
#for-root .tpl-bar.w80{width:80%}#for-root .tpl-bar.w60{width:60%}#for-root .tpl-bar.w45{width:45%}#for-root .tpl-bar.w30{width:30%}
#for-root .tpl-bar.tall{height:42px}
#for-root .tpl-bar.btn{height:20px;width:70px;border-radius:5px;background:rgba(99,102,241,.25)}
#for-root .tpl-row{display:flex;gap:6px}
#for-root .tpl-sq{width:54px;height:54px;border-radius:7px;background:var(--line);flex-shrink:0}
#for-root .tpl-lines{flex:1;display:flex;flex-direction:column;gap:5px;justify-content:center}
#for-root .tpl-info{padding:14px 16px}
#for-root .tpl-name{font-weight:700;font-size:14px;color:var(--ink);margin-bottom:5px}
#for-root .tpl-tags{display:flex;gap:6px;flex-wrap:wrap}
#for-root .tpl-tag{font-size:11px;font-weight:600;color:var(--accent);background:var(--soft);border:1px solid rgba(99,102,241,.2);padding:2px 8px;border-radius:20px}

/* niche links */
#for-root .niche-links{display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:0}
#for-root .niche-link{font-size:13px;font-weight:600;color:var(--muted);padding:7px 16px;border:1px solid var(--line);border-radius:20px;cursor:pointer;transition:all .18s;background:var(--paper)}
#for-root .niche-link:hover{border-color:rgba(99,102,241,.4);color:var(--accent);background:var(--soft)}
#for-root .niche-link.active{border-color:var(--accent);color:var(--accent);background:var(--soft)}

/* CTA */
#for-root .cta-section{padding:80px 0;background:var(--accent);text-align:center;position:relative;overflow:hidden}
#for-root .cta-section::before{content:"";position:absolute;inset:0;background:radial-gradient(580px 240px at 50% 110%,rgba(255,255,255,.07),transparent 70%);pointer-events:none}
#for-root .cta-section .container{position:relative;z-index:1}
#for-root .cta-section h2{font-family:var(--fd);font-weight:800;font-size:clamp(26px,4vw,40px);letter-spacing:-.03em;color:#fff;margin-bottom:14px;line-height:1.1}
#for-root .cta-section p{color:rgba(255,255,255,.75);font-size:16px;margin-bottom:32px;max-width:480px;margin-left:auto;margin-right:auto}
#for-root .cta-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
#for-root .cta-btn{padding:13px 28px;border-radius:11px;font-size:14.5px;font-weight:700;cursor:pointer;font-family:var(--fb);transition:all .2s}
#for-root .cta-btn.white{background:#fff;color:var(--accent);border:0}
#for-root .cta-btn.white:hover{transform:translateY(-2px);box-shadow:0 12px 32px rgba(0,0,0,.2)}
#for-root .cta-btn.outline{background:transparent;border:1px solid rgba(255,255,255,.4);color:#fff}
#for-root .cta-btn.outline:hover{background:rgba(255,255,255,.1);transform:translateY(-2px)}

/* footer */
#for-root footer{background:var(--paper);border-top:1px solid var(--line);padding:36px 0}
#for-root .foot-in{display:flex;flex-direction:column;gap:24px;align-items:center;text-align:center}
#for-root .foot-top{display:flex;align-items:center;justify-content:space-between;width:100%;flex-wrap:wrap;gap:16px}
#for-root .foot-copy{font-size:12.5px;color:var(--faint)}
#for-root .foot-pages{display:flex;gap:4px;flex-wrap:wrap;justify-content:center}
#for-root .foot-niche-label{font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--faint);margin-bottom:10px}
#for-root .foot-link{font-size:13px;color:var(--muted);padding:5px 12px;border-radius:20px;transition:all .15s;cursor:pointer}
#for-root .foot-link:hover{color:var(--ink);background:var(--bg)}

@media(max-width:720px){
  #for-root .fgrid{grid-template-columns:1fr 1fr}
  #for-root .tpl-grid{grid-template-columns:1fr}
  #for-root .uc-grid{grid-template-columns:1fr}
  #for-root .steps{grid-template-columns:1fr;gap:20px}
  #for-root .steps::before{display:none}
  #for-root .step{padding:0}
  #for-root .quote-card{padding:28px 24px}
  #for-root .foot-top{flex-direction:column;align-items:center}
}
@media(max-width:480px){
  #for-root .fgrid{grid-template-columns:1fr}
  #for-root .hero-meta{gap:12px}
}
`

// ── Types ────────────────────────────────────────────────────────────────────

interface Feature { ico: string; title: string; desc: string }
interface Template { name: string; tag: string; color: string; preview: 'landing' | 'store' | 'portfolio' | 'blog' | 'restaurant' | 'startup' }
interface NicheData {
  slug: string
  label: string
  title: string
  titleAccent: string
  sub: string
  emoji: string
  badge: string
  features: Feature[]
  useCases: string[]
  quote: { text: string; av: string; name: string; role: string }
  templates: Template[]
  ctaTitle: string
  ctaSub: string
}

// ── Template preview ─────────────────────────────────────────────────────────

function TplPreview({ type, color }: { type: Template['preview']; color: string }) {
  const bg = `${color}18`
  if (type === 'landing' || type === 'startup') return (
    <div className="tpl-thumb-inner" style={{ background: bg }}>
      <div className="tpl-bar w80" />
      <div className="tpl-bar w60" />
      <div className="tpl-bar w45" />
      <div className="tpl-bar btn" style={{ marginTop: 4 }} />
      <div className="tpl-row" style={{ marginTop: 8, gap: 6 }}>
        {[0,1,2].map(i => <div key={i} style={{ flex:1, height:50, borderRadius:8, background:'var(--line)' }} />)}
      </div>
    </div>
  )
  if (type === 'store') return (
    <div className="tpl-thumb-inner" style={{ background: bg }}>
      <div className="tpl-row" style={{ marginBottom: 4 }}>
        <div className="tpl-bar w30" /><div style={{ flex:1 }} /><div className="tpl-bar w30" />
      </div>
      <div className="tpl-row" style={{ gap:6 }}>
        {[0,1].map(i => <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', gap:5 }}>
          <div style={{ height:60, borderRadius:8, background:'var(--line)' }} />
          <div className="tpl-bar w80" /><div className="tpl-bar w60" />
        </div>)}
      </div>
    </div>
  )
  if (type === 'portfolio') return (
    <div className="tpl-thumb-inner" style={{ background: bg }}>
      <div className="tpl-bar w45" style={{ marginBottom: 6 }} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, flex:1 }}>
        <div style={{ gridRow:'1/3', borderRadius:8, background:'var(--line)' }} />
        <div style={{ borderRadius:8, background:'var(--line)' }} />
        <div style={{ borderRadius:8, background:'var(--line)' }} />
      </div>
    </div>
  )
  if (type === 'restaurant') return (
    <div className="tpl-thumb-inner" style={{ background: bg }}>
      <div className="tpl-bar w60" style={{ marginBottom: 6 }} />
      <div style={{ height: 70, borderRadius:8, background:'var(--line)', marginBottom:6 }} />
      <div className="tpl-row" style={{ gap:6 }}>
        {[0,1,2].map(i => <div key={i} style={{ flex:1, height:34, borderRadius:7, background:'var(--line)' }} />)}
      </div>
    </div>
  )
  if (type === 'blog') return (
    <div className="tpl-thumb-inner" style={{ background: bg }}>
      {[0,1,2].map(i => (
        <div key={i} className="tpl-row" style={{ gap:8, marginBottom:6 }}>
          <div className="tpl-sq" style={{ width:40, height:36 }} />
          <div className="tpl-lines"><div className="tpl-bar w80" /><div className="tpl-bar w60" /></div>
        </div>
      ))}
    </div>
  )
  return <div style={{ flex:1, background: bg }} />
}

// ── Niche navigation ─────────────────────────────────────────────────────────

const ALL_NICHES = [
  { slug: 'restaurants', label: '🍽 Рестораны' },
  { slug: 'agencies',    label: '🏢 Агентства' },
  { slug: 'freelancers', label: '💼 Фрилансеры' },
  { slug: 'startups',    label: '🚀 Стартапы' },
  { slug: 'ecommerce',   label: '🛍 Магазины' },
  { slug: 'portfolios',  label: '🎨 Портфолио' },
]

// ── ForPage component ────────────────────────────────────────────────────────

function ForPage({ data }: { data: NicheData }) {
  const navigate = useNavigate()
  const [theme, setTheme] = useState<Theme>('light')
  const [scrolled, setScrolled] = useState(false)
  const revealRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const saved = localStorage.getItem('bst-lp-theme') as Theme | null
    if (saved === 'dark' || saved === 'light') setTheme(saved)
    else if (window.matchMedia('(prefers-color-scheme: dark)').matches) setTheme('dark')
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.documentElement.classList.remove('dark')
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.height = ''
    }
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const els = document.querySelectorAll('#for-root .reveal')
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { (e.target as HTMLElement).classList.add('in'); io.unobserve(e.target) } }),
      { threshold: 0.12 }
    )
    els.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [data.slug])

  const toggleTheme = () => setTheme(t => {
    const next: Theme = t === 'light' ? 'dark' : 'light'
    localStorage.setItem('bst-lp-theme', next)
    return next
  })

  useEffect(() => {
    // Update page title and meta description per niche for SEO
    document.title = `BST для ${data.label} — Конструктор сайтов без кода`
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) metaDesc.setAttribute('content', data.sub)
    const metaOg = document.querySelector('meta[property="og:title"]')
    if (metaOg) metaOg.setAttribute('content', `BST для ${data.label}`)
    const canonical = document.querySelector('link[rel="canonical"]')
    if (canonical) canonical.setAttribute('href', `https://mybst.ru/for/${data.slug}`)
    return () => {
      document.title = 'BST — Конструктор сайтов: 376+ блоков, drag&drop, AI'
      if (metaDesc) metaDesc.setAttribute('content', 'Визуальный конструктор сайтов с 376+ блоками, AI-ассистентом и drag&drop. Создайте профессиональный сайт за 5 минут без кода. Бесплатно.')
      if (canonical) canonical.setAttribute('href', 'https://mybst.ru/')
    }
  }, [data])

  const go = () => navigate('/login')

  return (
    <div id="for-root" data-theme={theme} ref={revealRef}>
      <style>{FOR_CSS}</style>

      {/* ── Header ── */}
      <header className={scrolled ? 'scrolled' : ''}>
        <div className="container h-in">
          <div className="brand" onClick={() => navigate('/')}>
            <span className="brand-mark">B</span>
            <span className="brand-name">BST</span>
          </div>
          <span className="sep">/</span>
          <span className="crumb">Для {data.label.toLowerCase()}</span>
          <div className="h-right">
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'light'
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4.5"/><path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/></svg>
              }
            </button>
            <button className="btn-h ghost" onClick={() => navigate('/')}>← Главная</button>
            <button className="btn-h primary" onClick={go}>Попробовать бесплатно</button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="container">
          <span className="niche-emoji">{data.emoji}</span>
          <div className="niche-badge">BST · {data.badge}</div>
          <h1>
            {data.title}{' '}
            <span className="acc">{data.titleAccent}</span>
          </h1>
          <p className="hero-sub">{data.sub}</p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={go}>
              Создать сайт бесплатно
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </button>
            <button className="btn-ghost-lg" onClick={() => navigate('/showcase')}>
              Смотреть примеры
            </button>
          </div>
          <div className="hero-meta">
            <span className="meta-pill"><span className="meta-dot"/>Бесплатный план</span>
            <span className="meta-pill"><span className="meta-dot"/>376+ блоков</span>
            <span className="meta-pill"><span className="meta-dot"/>Без кода</span>
            <span className="meta-pill"><span className="meta-dot"/>Готово за 5 минут</span>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="section">
        <div className="container">
          <div className="sec-head">
            <span className="kicker">Возможности</span>
            <h2 className="sec-title">Всё, что нужно {data.label.toLowerCase()}</h2>
            <p className="sec-sub">Специально подобранные инструменты для вашей ниши — готовы к работе сразу после регистрации.</p>
          </div>
          <div className="fgrid">
            {data.features.map(({ ico, title, desc }, i) => (
              <div className="fcell reveal" key={title} style={{ '--d': `${(i % 3) * 0.07}s` } as React.CSSProperties}>
                <div className="f-ico">{ico}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use cases ── */}
      <section className="section tinted" style={{ paddingTop: 64, paddingBottom: 64 }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }}>
            <div>
              <span className="kicker">Задачи</span>
              <h2 className="sec-title" style={{ textAlign: 'left', marginBottom: 20, marginTop: 10 }}>
                Решает реальные задачи
              </h2>
              <p className="sec-sub" style={{ textAlign: 'left', marginLeft: 0, marginBottom: 24 }}>
                BST закрывает потребности {data.label.toLowerCase()} от первого экрана до оплаты.
              </p>
            </div>
            <div className="uc-grid">
              {data.useCases.map((uc, i) => (
                <div className="uc-item reveal" key={uc} style={{ '--d': `${i * 0.06}s` } as React.CSSProperties}>
                  <span className="uc-check">✓</span>
                  <span className="uc-text">{uc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonial ── */}
      <section className="section" style={{ paddingTop: 64, paddingBottom: 64 }}>
        <div className="container">
          <div className="reveal">
            <div className="quote-card">
              <p className="quote-text">{data.quote.text}</p>
              <div className="quote-foot">
                <span className="quote-av">{data.quote.av}</span>
                <div>
                  <div className="quote-name">{data.quote.name}</div>
                  <div className="quote-role">{data.quote.role}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Templates ── */}
      <section className="section tinted">
        <div className="container">
          <div className="sec-head">
            <span className="kicker">Шаблоны</span>
            <h2 className="sec-title">Начните с готового шаблона</h2>
            <p className="sec-sub">Каждый шаблон полностью редактируется — тексты, цвета, блоки.</p>
          </div>
          <div className="tpl-grid">
            {data.templates.map(({ name, tag, color, preview }, i) => (
              <div
                className="tpl-card reveal"
                key={name}
                style={{ '--d': `${i * 0.08}s` } as React.CSSProperties}
                onClick={go}
              >
                <div className="tpl-thumb" style={{ borderBottom: '1px solid var(--line)' }}>
                  <TplPreview type={preview} color={color} />
                </div>
                <div className="tpl-info">
                  <div className="tpl-name">{name}</div>
                  <div className="tpl-tags">
                    <span className="tpl-tag">{tag}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Steps ── */}
      <section className="section">
        <div className="container">
          <div className="sec-head">
            <span className="kicker">Как начать</span>
            <h2 className="sec-title">Три шага до готового сайта</h2>
          </div>
          <div className="steps">
            {[
              { n: '01', title: 'Выберите шаблон', desc: 'Откройте библиотеку шаблонов и выберите подходящий для вашей ниши.' },
              { n: '02', title: 'Настройте под себя', desc: 'Редактируйте тексты, цвета, блоки и изображения прямо на холсте.' },
              { n: '03', title: 'Опубликуйте', desc: 'Нажмите Publish — сайт уходит в интернет с SSL и CDN за секунды.' },
            ].map(({ n, title, desc }, i) => (
              <div className="step reveal" key={n} style={{ '--d': `${i * 0.1}s` } as React.CSSProperties}>
                <div className="step-num">{n}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Other niches ── */}
      <section className="section tinted" style={{ paddingTop: 48, paddingBottom: 56 }}>
        <div className="container">
          <div className="sec-head" style={{ marginBottom: 24 }}>
            <span className="kicker">Другие ниши</span>
            <h2 className="sec-title" style={{ fontSize: 24 }}>BST подходит для любого проекта</h2>
          </div>
          <div className="niche-links">
            {ALL_NICHES.map(({ slug, label }) => (
              <button
                key={slug}
                className={`niche-link${slug === data.slug ? ' active' : ''}`}
                onClick={() => navigate(`/for/${slug}`)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container">
          <h2>{data.ctaTitle}</h2>
          <p>{data.ctaSub}</p>
          <div className="cta-btns">
            <button className="cta-btn white" onClick={go}>
              Создать сайт бесплатно →
            </button>
            <button className="cta-btn outline" onClick={() => navigate('/showcase')}>
              Посмотреть витрину
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer>
        <div className="container foot-in">
          <div className="foot-top">
            <div className="brand" onClick={() => navigate('/')}>
              <span className="brand-mark">B</span>
              <span className="brand-name">BST</span>
            </div>
            <div className="foot-pages">
              <span className="foot-link" onClick={() => navigate('/')}>Главная</span>
              <span className="foot-link" onClick={() => navigate('/showcase')}>Витрина</span>
              <span className="foot-link" onClick={() => navigate('/changelog')}>Changelog</span>
              <span className="foot-link" onClick={() => navigate('/login')}>Войти</span>
            </div>
            <span className="foot-copy">© 2026 BST</span>
          </div>
          <div>
            <div className="foot-niche-label">Для кого</div>
            <div className="foot-pages">
              {ALL_NICHES.map(({ slug, label }) => (
                <span key={slug} className="foot-link" onClick={() => navigate(`/for/${slug}`)}>{label}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// NICHE DATA
// ══════════════════════════════════════════════════════════════════════════════

const RESTAURANTS: NicheData = {
  slug: 'restaurants',
  label: 'Ресторанов',
  title: 'Сайт для ресторана',
  titleAccent: 'за один вечер',
  sub: 'Онлайн-меню, форма бронирования, галерея блюд и карта — всё готово. Без программистов и дизайнеров.',
  emoji: '🍽',
  badge: 'Рестораны и кафе',
  features: [
    { ico: '📋', title: 'Онлайн-меню с фото', desc: 'Каталог блюд с категориями, описаниями и ценами. Обновляйте меню в один клик — без разработчика.' },
    { ico: '📅', title: 'Бронирование столика', desc: 'Встроенная форма резервирования с выбором даты и количества гостей. Заявки приходят в Telegram мгновенно.' },
    { ico: '🖼', title: 'Галерея блюд и интерьера', desc: 'Masonry-галерея, lightbox, слайдер с фото зала — привлекайте гостей красивыми визуальными образами.' },
    { ico: '🗺', title: 'Карта и контакты', desc: 'Встроенная Яндекс.Карта, часы работы, телефон и адрес — гость найдёт вас без усилий.' },
    { ico: '💬', title: 'WhatsApp и Telegram', desc: 'Кнопки быстрой связи: WhatsApp, Telegram, телефон. Клиент делает заказ в один тап.' },
    { ico: '⭐', title: 'Отзывы и рейтинг', desc: 'Блок отзывов с фото, рейтинговыми звёздами и переходом на Яндекс.Карты или Google Maps.' },
  ],
  useCases: [
    'Продвижение в Яндекс и Google',
    'Показ меню на телефоне гостя',
    'Онлайн-резервирование столиков',
    'Галерея блюд и интерьера',
    'Акции и спецпредложения',
    'Доставка и самовывоз',
    'Контакты и схема проезда',
    'Интеграция с JivoChat',
  ],
  quote: {
    text: 'Раньше наше меню жило в PDF-файле. Теперь у нас полноценный сайт с бронированием и галереей. Гости говорят, что нашли нас именно через красивые фото блюд.',
    av: 'АК',
    name: 'Анна Ковалёва',
    role: 'Владелец · Кафе «Уют»',
  },
  templates: [
    { name: 'Ресторан "Классика"', tag: 'Ресторан', color: '#f59e0b', preview: 'restaurant' },
    { name: 'Кафе "Минимал"', tag: 'Кафе', color: '#10b981', preview: 'restaurant' },
    { name: 'Доставка еды', tag: 'Доставка', color: '#ef4444', preview: 'store' },
  ],
  ctaTitle: 'Ваш ресторан заслуживает красивого сайта',
  ctaSub: 'Создайте сайт ресторана за один вечер. Бесплатно, без кода.',
}

const AGENCIES: NicheData = {
  slug: 'agencies',
  label: 'Агентств',
  title: 'Платформа для',
  titleAccent: 'digital-агентств',
  sub: 'Управляйте сотнями клиентских сайтов из одной панели. White Label, командный доступ, HTML-экспорт.',
  emoji: '🏢',
  badge: 'Агентства и студии',
  features: [
    { ico: '🏷', title: 'White Label', desc: 'Переименуйте BST под свой бренд: логотип, цвета, домен. Клиент видит ваш продукт, не наш.' },
    { ico: '👥', title: 'Командный доступ', desc: 'Добавляйте дизайнеров, менеджеров и клиентов с разными правами: Редактор, Просмотр.' },
    { ico: '📦', title: 'HTML-экспорт', desc: 'Скачайте готовый сайт как чистый HTML/CSS/JS и передайте клиенту или разместите где угодно.' },
    { ico: '🗂', title: 'Управление множеством сайтов', desc: 'Agency-план — неограниченное количество сайтов. Все клиенты в одной панели, переключайтесь мгновенно.' },
    { ico: '🔌', title: 'Headless CMS + API', desc: 'Динамический контент через REST API. Подключайте к любому фронтенду, мобильным приложениям, внешним сервисам.' },
    { ico: '💰', title: 'Реферальная программа', desc: 'Получайте 30% от каждого клиента, которого привели в BST. Пассивный доход без усилий.' },
  ],
  useCases: [
    'Сайты под ключ за 1-3 дня',
    'White Label под вашим брендом',
    'Несколько клиентов в одной панели',
    'Командная работа (дизайнер + клиент)',
    'Экспорт HTML/CSS для передачи',
    'CMS для клиентского контента',
    'Аналитика и A/B тесты',
    'Реферальный доход от клиентов',
  ],
  quote: {
    text: 'Мы закрываем сайты для клиентов за 2-3 дня вместо двух недель. White Label позволяет выглядеть профессионально — клиенты думают, что это наша собственная платформа.',
    av: 'ДС',
    name: 'Дмитрий Сергеев',
    role: 'CEO · Digital-студия «PageLab»',
  },
  templates: [
    { name: 'Агентство "Модерн"', tag: 'Агентство', color: '#6366f1', preview: 'landing' },
    { name: 'IT-компания', tag: 'IT', color: '#3b82f6', preview: 'startup' },
    { name: 'Портфолио студии', tag: 'Студия', color: '#8b5cf6', preview: 'portfolio' },
  ],
  ctaTitle: 'Масштабируйте агентство с BST',
  ctaSub: 'White Label, неограниченные сайты, командный доступ — всё в Agency-плане.',
}

const FREELANCERS: NicheData = {
  slug: 'freelancers',
  label: 'Фрилансеров',
  title: 'Сайт-портфолио',
  titleAccent: 'за 5 минут',
  sub: 'Профессиональное портфолио с формой заявки, кейсами и ценами. Покажите лучшие работы — получите новых клиентов.',
  emoji: '💼',
  badge: 'Фрилансеры и специалисты',
  features: [
    { ico: '🎨', title: 'Портфолио и кейсы', desc: 'Masonry-галерея, детальные страницы проектов, before/after слайдер. Покажите работы в лучшем свете.' },
    { ico: '📝', title: 'Форма заявки и контакты', desc: 'Настраиваемая форма с уведомлением в Telegram. Клиент оставляет заявку — вы получаете уведомление секунду спустя.' },
    { ico: '💲', title: 'Прайс-лист', desc: 'Блок с тарифами, что входит в каждый пакет, кнопка «Заказать». Работает как полноценная страница продаж.' },
    { ico: '⭐', title: 'Отзывы клиентов', desc: 'Блоки отзывов со звёздами, фото и ссылками на работы. Социальное доказательство = больше конверсий.' },
    { ico: '🌐', title: 'Мультиязычность', desc: 'Переведите портфолио на 15 языков одной кнопкой. Работайте с международными клиентами без барьеров.' },
    { ico: '📊', title: 'Встроенная аналитика', desc: 'Смотрите, кто и откуда приходит на ваш сайт. Heatmap кликов и воронки показывают, что работает.' },
  ],
  useCases: [
    'Показ кейсов и работ клиентам',
    'Приём заявок в Telegram',
    'Прайс-лист с пакетами услуг',
    'Биография и стек технологий',
    'Блог для SEO-продвижения',
    'Форма обратного звонка',
    'Резюме для удалённой работы',
    'Международные клиенты (15 языков)',
  ],
  quote: {
    text: 'До BST я отправлял клиентам папку с PDF-кейсами. Теперь у меня сайт-портфолио на своём домене, заявки приходят в Telegram, и я уже взял первый зарубежный проект.',
    av: 'МВ',
    name: 'Максим Волков',
    role: 'UI/UX дизайнер · Фрилансер',
  },
  templates: [
    { name: 'Дизайнер "Studio"', tag: 'Портфолио', color: '#ec4899', preview: 'portfolio' },
    { name: 'Разработчик "Code"', tag: 'Dev', color: '#6366f1', preview: 'portfolio' },
    { name: 'Фотограф "Light"', tag: 'Фото', color: '#f59e0b', preview: 'portfolio' },
  ],
  ctaTitle: 'Ваше портфолио привлечёт новых клиентов',
  ctaSub: 'Красивый сайт-портфолио с формой заявки — бесплатно за 5 минут.',
}

const STARTUPS: NicheData = {
  slug: 'startups',
  label: 'Стартапов',
  title: 'Лендинг для стартапа',
  titleAccent: 'за один вечер',
  sub: 'Проверяйте гипотезы быстро. Waitlist, A/B тесты, аналитика конверсий — всё включено. Без разработчиков.',
  emoji: '🚀',
  badge: 'Стартапы и продукты',
  features: [
    { ico: '⚡', title: 'Запуск за один вечер', desc: 'Выбрали шаблон → отредактировали тексты → опубликовали. Через час ваш лендинг уже живёт в интернете.' },
    { ico: '📋', title: 'Waitlist и pre-launch', desc: 'Встроенный блок сбора email-листа ожидания. Собирайте аудиторию до запуска продукта.' },
    { ico: '🧪', title: 'A/B тестирование', desc: 'Тестируйте разные заголовки, CTA и офферы. Встроенный A/B-движок показывает, что конвертирует лучше.' },
    { ico: '📊', title: 'Аналитика и воронки', desc: 'Heatmap кликов, session recording, конверсионные воронки. Понимайте пользователей без Google Analytics.' },
    { ico: '🤖', title: 'AI-генерация за минуты', desc: 'Опишите продукт одной фразой — AI напишет тексты для всех блоков, SEO-метатеги и заголовки.' },
    { ico: '💳', title: 'Приём платежей', desc: 'ЮКасса из коробки. Принимайте предзаказы и оплату прямо с лендинга без магазина.' },
  ],
  useCases: [
    'Проверка гипотез за 1 день',
    'Сбор waitlist до запуска',
    'A/B тест заголовков и офферов',
    'Приём предзаказов и платежей',
    'Аналитика конверсий',
    'SEO с первого дня',
    'Интеграция с Product Hunt',
    'AI-генерация текстов',
  ],
  quote: {
    text: 'Мы запустили лендинг в воскресенье вечером, к утру понедельника уже 200 человек в waitlist. A/B тест показал, что второй заголовок конвертирует на 40% лучше.',
    av: 'ТН',
    name: 'Тарас Нечипоренко',
    role: 'Co-founder · TaskFlow',
  },
  templates: [
    { name: 'SaaS Landing', tag: 'SaaS', color: '#6366f1', preview: 'startup' },
    { name: 'Product Hunt Ready', tag: 'Продукт', color: '#f59e0b', preview: 'landing' },
    { name: 'App Waitlist', tag: 'Waitlist', color: '#10b981', preview: 'landing' },
  ],
  ctaTitle: 'Запустите лендинг стартапа сегодня',
  ctaSub: 'A/B тесты, waitlist, аналитика — всё готово. Бесплатно.',
}

const ECOMMERCE: NicheData = {
  slug: 'ecommerce',
  label: 'Интернет-магазинов',
  title: 'Магазин с нуля',
  titleAccent: 'без разработчика',
  sub: 'Каталог, корзина, промокоды, доставка, аналитика продаж. Полноценный e-commerce — за один день.',
  emoji: '🛍',
  badge: 'Интернет-магазины',
  features: [
    { ico: '🛒', title: 'Каталог и корзина', desc: 'Товары с фото, категориями, вариантами (размер, цвет). Корзина и оформление заказа — встроено.' },
    { ico: '🏷', title: 'Промокоды и скидки', desc: 'Создавайте промокоды на % или сумму, с лимитом использования и датой окончания. Запускайте акции за минуту.' },
    { ico: '🚚', title: 'Зоны доставки', desc: 'Настройте зоны доставки с разными тарифами. Бесплатная доставка при сумме заказа — включено.' },
    { ico: '💳', title: 'ЮКасса', desc: 'Принимайте оплату картой, СБП, QR-кодом — из коробки, без дополнительных сервисов.' },
    { ico: '📊', title: 'Аналитика продаж', desc: 'Dashboard с выручкой, средним чеком, популярными товарами. Экспорт в 1С и WooCommerce.' },
    { ico: '📦', title: 'Цифровые товары', desc: 'Продавайте курсы, PDF, шаблоны. Ссылка на скачивание отправляется покупателю автоматически после оплаты.' },
  ],
  useCases: [
    'Продажа товаров онлайн',
    'Цифровые товары и курсы',
    'Управление заказами и складом',
    'Промокоды и распродажи',
    'Приём оплаты через ЮКассу',
    'Уведомления о заказах в Telegram',
    'Экспорт в 1С / WooCommerce',
    'SEO продуктовых страниц',
  ],
  quote: {
    text: 'Открыл магазин за день — каталог, корзина, оплата картой. Первый заказ получил уже через три часа после запуска. BST заменил мне и разработчика, и хостинг.',
    av: 'КЛ',
    name: 'Кирилл Лебедев',
    role: 'Предприниматель · «Craft Store»',
  },
  templates: [
    { name: 'Магазин одежды', tag: 'Одежда', color: '#ec4899', preview: 'store' },
    { name: 'Цифровые товары', tag: 'Digital', color: '#6366f1', preview: 'store' },
    { name: 'Хенд-мейд магазин', tag: 'Craft', color: '#f59e0b', preview: 'store' },
  ],
  ctaTitle: 'Откройте магазин прямо сейчас',
  ctaSub: 'Каталог, оплата, доставка — всё готово. Первый товар добавьте через 5 минут.',
}

const PORTFOLIOS: NicheData = {
  slug: 'portfolios',
  label: 'Портфолио',
  title: 'Портфолио, которое',
  titleAccent: 'говорит само за себя',
  sub: 'Для дизайнеров, фотографов, архитекторов, художников. Красивые шаблоны, готовые блоки, свой домен.',
  emoji: '🎨',
  badge: 'Творческие профессии',
  features: [
    { ico: '🖼', title: 'Masonry-галерея', desc: 'Динамическая сетка работ с lightbox, фильтрацией по категориям и lazy loading. Загружается молниеносно.' },
    { ico: '🎭', title: '9 стилей оформления', desc: 'Swiss Grid, Dark Luxury, Neo-Brutalism, Minimal Air и другие. Переключайте стиль без потери контента.' },
    { ico: '📱', title: 'Адаптивный дизайн', desc: 'Одинаково красиво на телефоне, планшете и десктопе. Никаких правок под мобильную версию.' },
    { ico: '⚡', title: 'Lighthouse 90+', desc: 'Ваши работы загружаются быстро — это влияет на SEO и первое впечатление о профессионализме.' },
    { ico: '🌍', title: '15 языков', desc: 'Работайте с международными клиентами. Переведите портфолио на английский, немецкий, китайский за один клик.' },
    { ico: '📧', title: 'Форма для заявок', desc: 'Контактная форма с уведомлением в Telegram. Клиент написал — вы получили уведомление мгновенно.' },
  ],
  useCases: [
    'Показ работ потенциальным клиентам',
    'Приём заявок от клиентов',
    'Международное портфолио (15 языков)',
    'Страница «Обо мне» и услуги',
    'Прайс-лист на услуги',
    'SEO-продвижение по запросам',
    'Блог для привлечения аудитории',
    'Продажа принтов и цифровых работ',
  ],
  quote: {
    text: 'Я фотограф. Раньше показывала работы в Instagram. Теперь у меня сайт с gallerей на 200 фото, формой для заявок и английской версией. Пришли клиенты из Германии.',
    av: 'НА',
    name: 'Наталья Арефьева',
    role: 'Фотограф · nat-photo.ru',
  },
  templates: [
    { name: 'Photographer Portfolio', tag: 'Фото', color: '#1f2937', preview: 'portfolio' },
    { name: 'Designer Studio', tag: 'Дизайн', color: '#6366f1', preview: 'portfolio' },
    { name: 'Art Gallery', tag: 'Арт', color: '#ec4899', preview: 'portfolio' },
  ],
  ctaTitle: 'Создайте портфолио, которым гордятся',
  ctaSub: 'Красивые шаблоны для творческих специалистов — бесплатно.',
}

// ── Exports ──────────────────────────────────────────────────────────────────

export function ForRestaurantsPage() { return <ForPage data={RESTAURANTS} /> }
export function ForAgenciesPage()    { return <ForPage data={AGENCIES} /> }
export function ForFreelancersPage() { return <ForPage data={FREELANCERS} /> }
export function ForStartupsPage()    { return <ForPage data={STARTUPS} /> }
export function ForEcommercePage()   { return <ForPage data={ECOMMERCE} /> }
export function ForPortfoliosPage()  { return <ForPage data={PORTFOLIOS} /> }
